/**
 * FAQChatbotPanel.jsx — Rê Leve IA (Etapa 11)
 *
 * Segundo protótipo: Chatbot FAQ.
 * - Usa API real (POST /api/faq) se disponível, com useCase=faq.
 * - Se a API falhar, cai no motor local (faqChatbotEngine).
 * - Site do Cliente nunca exibe trace técnico.
 * - Harness e guardrails ativos pelos dois lados (backend e local).
 */

import { useState } from 'react';
import {
  generateFAQResponse,
  getFAQQuickQuestions,
} from '../lib/faqChatbotEngine';
import { sendFAQToApi } from '../lib/apiClient';
import { recordChatEvent } from '../lib/chatEventsLogger';
import appConfig from '../config/appConfig';

function nowLabel() {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function FAQChatbotPanel({ clienteId = 'GUEST_DEMO' }) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'bot',
      text: 'Olá! Sou o FAQ Rê Leve. Posso responder dúvidas frequentes sobre kits, calorias, pagamento, entrega, restrições alimentares e atendimento. O que você gostaria de saber?',
      timestamp: nowLabel(),
    },
  ]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [lastSource, setLastSource] = useState(null);

  const quickQuestions = getFAQQuickQuestions();

  async function askFAQ(message) {
    if (!message || !message.trim()) return;
    const userText = message.trim();
    setMessages(prev => [
      ...prev,
      { id: Date.now(), sender: 'user', text: userText, timestamp: nowLabel() },
    ]);
    recordChatEvent({
      role: 'user',
      text: userText,
      clienteId: clienteId || 'visitante',
      useCase: 'faq',
    });
    setInput('');
    setPending(true);

    let answer = null;
    let source = 'faq_local';

    // 1. Tenta API real (com useCase=faq) — se disponível
    if (appConfig.enableRealApi) {
      try {
        const api = await sendFAQToApi({
          clienteId,
          message: userText,
          source: 'client_site',
        });
        if (api && api.text) {
          answer = {
            text: api.text,
            cta: api.cta?.label || null,
            needsHuman: !!api.needsHuman,
          };
          source = api.llmUsed ? 'llm_backend' : (api.fallbackUsed ? 'deterministic_backend' : 'api_backend');
        }
      } catch {
        // Silencioso — cai para fallback local
      }
    }

    // 2. Fallback local
    if (!answer) {
      const local = generateFAQResponse(userText);
      answer = { text: local.text, cta: local.cta, needsHuman: local.needsHuman };
      source = local.source || 'faq_local';
    }

    setLastSource(source);
    setMessages(prev => [
      ...prev,
      {
        id: Date.now() + 1,
        sender: 'bot',
        text: answer.text,
        timestamp: nowLabel(),
        cta: answer.cta,
        needsHuman: answer.needsHuman,
        source,
      },
    ]);
    recordChatEvent({
      role: 'bot',
      text: answer.text,
      clienteId: clienteId || 'visitante',
      source,
      useCase: 'faq',
      intent: answer.needsHuman ? 'handoff_humano' : 'faq_response',
    });
    setPending(false);
  }

  function sourceBadge(source) {
    if (!source) return null;
    if (source === 'llm_backend') {
      return <span className="response-source-badge response-source-badge--llm">🧠 LLM via backend</span>;
    }
    if (source === 'deterministic_backend' || source === 'api_backend') {
      return <span className="response-source-badge response-source-badge--deterministic">⚙️ Motor determinístico</span>;
    }
    return <span className="response-source-badge response-source-badge--fallback">📋 Fallback local</span>;
  }

  function handleQuick(q) {
    askFAQ(q.message);
  }

  function handleSend() {
    askFAQ(input);
  }

  return (
    <div className="cs-chat" style={{ borderTop: '3px solid #0ea5e9' }}>
      <div className="cs-chat-header" style={{ background: '#e0f2fe' }}>
        <span className="cs-chat-avatar">❓</span>
        <div style={{ flex: 1 }}>
          <p className="cs-chat-name">Chatbot FAQ Rê Leve</p>
          <p className="cs-chat-status">
            Protótipo de atendimento automático para dúvidas frequentes
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 10,
            background: '#dcfce7', color: '#166534', fontWeight: 600,
          }}>
            Harness ativo
          </span>
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 10,
            background: '#fef3c7', color: '#92400e', fontWeight: 600,
          }}>
            Fallback seguro
          </span>
        </div>
      </div>

      <div className="cs-chat-messages">
        {messages.map(m => (
          <div key={m.id}>
            <div className={`cs-bubble cs-bubble--${m.sender}`}>{m.text}</div>
            {m.sender === 'bot' && m.source && sourceBadge(m.source)}
            {m.sender === 'bot' && m.needsHuman && (
              <div className="cs-escalate-cta">
                <a href="#" className="cs-btn cs-btn--escalate">💬 Falar com atendimento</a>
              </div>
            )}
            {m.sender === 'bot' && m.cta && !m.needsHuman && (
              <div className="cs-whatsapp-cta">
                <button className="cs-btn cs-btn--whatsapp-inline" type="button">
                  {m.cta}
                </button>
              </div>
            )}
          </div>
        ))}
        {pending && (
          <div className="cs-bubble cs-bubble--bot" style={{ opacity: 0.6 }}>
            Buscando resposta...
          </div>
        )}
      </div>

      <div className="cs-chat-quick">
        {quickQuestions.map(q => (
          <button
            key={q.id}
            className="cs-quick-btn"
            onClick={() => handleQuick(q)}
            disabled={pending}
          >
            {q.label}
          </button>
        ))}
      </div>

      <div className="cs-chat-input-row">
        <input
          className="cs-chat-input"
          type="text"
          placeholder="Digite sua dúvida..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !pending && handleSend()}
          disabled={pending}
        />
        <button
          className="cs-btn cs-btn--send"
          onClick={handleSend}
          disabled={pending || !input.trim()}
        >
          Enviar
        </button>
      </div>

      <p style={{
        fontSize: 10, color: '#6b7280', textAlign: 'center',
        padding: '8px 12px 4px', lineHeight: 1.5,
      }}>
        Respostas em modo protótipo. Dúvidas operacionais reais devem ser confirmadas pelo atendimento humano.
      </p>
    </div>
  );
}
